import DistributionBids from "components/distributionBids/list";
import { Transition } from "components/shared/column";
import differenceInSeconds from "date-fns/differenceInSeconds";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import { carsPlanningPeriodTypesEnum } from "helpers/enums/carsPlanningPeriodTypes";
import { viewContainer } from "helpers/redux";
import { ymEvent } from "helpers/system";
import { useLocalStorage } from "hooks/useLocalStorage";
import Tooltip from "rc-tooltip";
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { getReport, getResponsibles, getTrailerTypes } from "sources/carsPlanning";
import { increase } from "sources/counter";
import { showBidCarsPlanningSettings } from "sources/profile/organization";
import { buildFilter } from "./utils";

const CarPlanningTimeline = lazy(() => import("./timeline.js"));

function CarPlanning(props) {
    const { match, location, history, organization, profile } = props;

    const [loading, setLoading] = useState(true);
    const [showOrders, setShowOrders] = useState(false);
    const [newDesign, setNewDesign] = useLocalStorage("car-planning-new-design", false);
    const [data, setData] = useState({});
    const [dataLoadedAt, setDataLoadedAt] = useState(null);

    const [sorted, setSorted] = useLocalStorage("car-planning-order", { field: "planEndDate", direction: "desc" });
    const [filtered, setFiltered] = useLocalStorage("car-planning-filtered", {});
    const [start, setStart] = useState(0);
    const [length, setLength] = useLocalStorage("car-planning-length", 15);
    const [itemsCount, setItemsCount] = useState(0);

    const refreshTimerRef = useRef(null);
    const updateFrequencyRef = useRef(300);
    const loadDataRef = useRef();

    useEffect(() => {
        ymEvent("PlanningClicked");
        increase({ Name: "PlanningOpens" });
        setData({});

        showBidCarsPlanningSettings().then((res) => {
            updateFrequencyRef.current = (res.updateFrequency || 5) * 60;
            createInterval();
            const { showAllDraftsInPlanning } = organization || {};
            if (showAllDraftsInPlanning) {
                setFiltered((prev) => ({ ...prev, showDraftInPlanning: showAllDraftsInPlanning }));
            }
            setTimeout(() => loadData(), 0);
        });

        window.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            clearInterval(refreshTimerRef.current);
            window.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    const createInterval = () => {
        if (document.visibilityState === "hidden") {
            return;
        }
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = setInterval(() => {
            if (loadDataRef.current) {
                loadDataRef.current();
            }
        }, updateFrequencyRef.current * 1000);
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            clearInterval(refreshTimerRef.current);
        } else {
            if (dataLoadedAt && differenceInSeconds(new Date(), new Date(dataLoadedAt)) > updateFrequencyRef.current) {
                loadData();
            }
            createInterval();
        }
    };

    const dataRequest = useCallback((...args) => {
        return new Promise((resolve) => {
            getReport(...args).then((reportData) => {
                Promise.all([getTrailerTypes(args[1]), getResponsibles(args[1])]).then(([trailerTypes, responsibles]) =>
                    resolve({
                        data: reportData,
                        trailerTypes: trailerTypes.reduce((acc, item) => {
                            acc[item.id] = item;
                            return acc;
                        }, {}),
                        responsibles: responsibles.reduce((acc, item) => {
                            acc[item.id] = item;
                            return acc;
                        }, {}),
                    }),
                );
            });
        });
    }, []);

    const loadData = () => {
        setLoading(true);
        const orderBy = sorted.field ? `${sorted.field} ${sorted.direction}` : undefined;
        const { showDraftInPlanning, period, ...rest } = filtered;
        const filter = buildFilter(rest, profile);

        return dataRequest(
            { start, length, orderBy, filter },
            {
                showDraftInPlanning: Boolean(showDraftInPlanning?.value),
                Period: period?.value || carsPlanningPeriodTypesEnum.CurrentMonth,
            },
        )
            .then((res) => {
                setData(res);
                setDataLoadedAt(Date.now());
                setItemsCount(res.data?.carsCount);
                // Если данных нет, явно сбрасываем loading
                if (!res || !res.data || !Array.isArray(res.data.data) || res.data.data.length === 0) {
                    setLoading(false);
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        loadDataRef.current = loadData;
    }, [loadData]);

    useEffect(() => {
        if (loading) {
            return;
        }
        loadData();
    }, [start, length, sorted, filtered]);

    const resetFilters = () => {
        setFiltered({});
        setSorted({ field: "planEndDate", direction: "desc" });
    };

    const onFilteredChange = ({ name, value, ...rest }) => {
        setFiltered((state) => {
            const newState = { ...state };
            if (!value || (Array.isArray(value) && value.length === 0)) {
                delete newState[name];
            } else {
                newState[name] = { name, value, ...rest };
            }
            return newState;
        });
        setStart(0);
    };

    const onSortedChange = (field, dir) => {
        setSorted({ field, direction: dir ?? (sorted.field === field && sorted.direction === "asc" ? "desc" : "asc") });
    };

    const changePage = (page) => setStart(page * length);

    const changePageSize = (pageSize) => {
        setStart(0);
        setLength(pageSize);
    };

    const paginationProps = {
        page: start / length,
        pages: Math.ceil(itemsCount / length),
        pageSize: length,
        onPageChange: changePage,
        onPageSizeChange: changePageSize,
        pageSizeOptions: [15, 30, 50, 100],
    };

    const renderHead = useCallback(() => {
        const dataFrom = dataLoadedAt ? new Date(dataLoadedAt) : null;
        return (
            <div className="book-show__head pl-0">
                <div className="book-show__title book-show__title--fixed-height w-100">
                    <div className="d-flex align-items-center h-100 justify-content-between">
                        <div>Планирование по машинам</div>
                        <div className="lh-sm text-right">
                            {dataFrom && (
                                <div className="text-muted text-nowrap small">
                                    Данные обновлены в{" "}
                                    {format(dataFrom, isToday(dataFrom) ? "HH:mm" : "dd.MM.yyyy HH:mm")}
                                </div>
                            )}
                            <div className="btn btn-xs btn-outline-primary" onClick={() => setNewDesign((p) => !p)}>
                                {newDesign ? "Вернуть старый дизайн" : "Попробовать новый дизайн"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [dataLoadedAt, newDesign]);

    const renderBodyWrap = () => {
        if (!dataLoadedAt) {
            return null;
        }
        return (
            <div className="book-list h-100">
                <Suspense fallback={<div className="loading" />}>
                    <CarPlanningTimeline
                        {...props}
                        {...{
                            data,
                            filtered,
                            sorted,
                            loading,
                            setLoading,
                            newDesign,
                            onFilteredChange,
                            onSortedChange,
                            paginationProps,
                            resetFilters,
                            loadData,
                        }}
                    />
                </Suspense>
            </div>
        );
    };

    return (
        <div className="page-content">
            <Transition dir={showOrders ? "half" : "full"} {...{ match, location, history }}>
                <div className="page-book__show book-show">
                    {renderHead()}
                    <div className="book-show__body">{renderBodyWrap()}</div>
                </div>
                <Tooltip overlay={showOrders ? "Скрыть заказы" : "Отобразить заказы"}>
                    <div className="p-orders__ear" onClick={() => setShowOrders((s) => !s)}>
                        <i className={showOrders ? "icon-uEAAE-angle-right-solid" : "icon-uEACD-angle-left"} />
                    </div>
                </Tooltip>
            </Transition>
            {showOrders && <DistributionBids transition="half" back={() => setShowOrders((s) => !s)} />}
        </div>
    );
}

export default viewContainer(CarPlanning, "planning");
